import { formatCents } from '@core/money'
import { remainingCents, stateLabel, type Payable, type PaymentState } from '@core/payments'
import type { Space } from '@core/session'
import { Card, Section } from '@design/components'
import { Tags } from '@design/Tags'
import { ExpectedSection } from './ExpectedSection'
import { RunBanner } from './RunBanner'
import { daysFromToday, todayIso } from '@core/dates'

/**
 * Upcoming — grouped by urgency, not by date order.
 *
 * The point of the screen is deciding what to do next, so anything that
 * already has a decision attached sinks to the bottom. Sections hide when
 * empty (rule 23), which is what lets this shrink to nothing on a quiet week
 * rather than showing a wall of zeroes.
 *
 * Estimates are kept out of every group and every total on purpose: a total
 * that quietly includes guesses is worse than no total (§10). They have their
 * own section at the bottom, with their own sum.
 */
export function UpcomingView({
  items,
  spaces,
  onOpen,
  onStartRun,
}: {
  items: Payable[]
  spaces: Space[]
  onOpen: (p: Payable) => void
  onStartRun: (spaceId: string) => void
}) {
  const live = items.filter((i) => !i.isEstimate)
  const expected = items.filter(
    (i) => i.isEstimate && i.state !== 'paid' && i.state !== 'cancelled',
  )
  const open = live.filter((i) => i.state !== 'paid' && i.state !== 'cancelled')

  const today = todayIso()
  const inAWeek = daysFromToday(7)

  const attention = open.filter((i) => i.state === 'needs_attention' && i.dueDate >= today)
  const overdue = open.filter((i) => i.dueDate < today)
  const thisWeek = open.filter(
    (i) => i.state !== 'needs_attention' && i.dueDate >= today && i.dueDate <= inAWeek,
  )
  const later = open.filter((i) => i.state !== 'needs_attention' && i.dueDate > inAWeek)

  const total = open.reduce((sum, i) => sum + remainingCents(i), 0)

  if (open.length === 0 && expected.length === 0) {
    return (
      <Card className="text-center py-8">
        <p className="text-lg font-medium mb-1">Niets openstaand</p>
        <p className="text-muted text-sm">Er is niets te betalen.</p>
      </Card>
    )
  }

  return (
    <>
      {open.length > 0 && (
        <p className="text-sm text-muted mb-4">
          Openstaand: <span className="font-medium text-ink">{formatCents(total)}</span>
        </p>
      )}

      {spaces.map((space) => (
        <RunBanner
          key={space.id}
          space={space}
          payables={items}
          onStarted={() => onStartRun(space.id)}
          onResume={() => onStartRun(space.id)}
        />
      ))}

      <Group title="Vraagt aandacht" items={attention} onOpen={onOpen} />
      <Group title="Te laat" items={overdue} onOpen={onOpen} />
      <Group title="Deze week" items={thisWeek} onOpen={onOpen} />
      <Group title="Later" items={later} onOpen={onOpen} />

      <ExpectedSection items={expected} />
    </>
  )
}

function Group({
  title,
  items,
  onOpen,
  muted = false,
}: {
  title: string
  items: Payable[]
  onOpen: (p: Payable) => void
  muted?: boolean
}) {
  if (items.length === 0) return null
  const sum = items.reduce((s, i) => s + remainingCents(i), 0)

  return (
    <Section title={title} count={items.length}>
      {!muted && (
        <p className="text-xs text-muted mb-2 px-1 tabular-nums">{formatCents(sum)}</p>
      )}
      <div className="space-y-2">
        {items.map((item) => (
          <button key={item.instalmentId} onClick={() => onOpen(item)} className="w-full text-left">
            <Card className={`flex items-center gap-3 ${muted ? 'opacity-60' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {item.supplierName ?? item.description}
                </p>
                <p className="text-sm text-muted">
                  {formatDate(item.dueDate)}
                  {item.plannedDate && ` · gepland ${formatDate(item.plannedDate)}`}
                </p>
                <Tags
                  spaceName={item.spaceName}
                  spaceKind={item.spaceKind}
                  category={item.category}
                />
                {item.isHard && (
                  <span className="inline-block mt-1 mr-1 text-xs px-2 py-0.5 rounded-full border border-warn/50 text-warn">
                    Kan niet wachten
                  </span>
                )}
                {item.state !== 'to_pay' && (
                  <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-line text-muted">
                    {stateLabel(item.state as PaymentState)}
                  </span>
                )}
              </div>
              <span className="shrink-0 tabular-nums font-medium">
                {formatCents(remainingCents(item))}
              </span>
            </Card>
          </button>
        ))}
      </div>
    </Section>
  )
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'short' }).format(
    new Date(`${iso}T00:00:00`),
  )
}
